import * as R from 'ramda';

import { DISJOINT_AVAILABLE_OFFER_TYPES } from './constants';
import { setTerms, setRootType, getTermsValue } from './helpers';
import { setRoomTypes } from './setRoomTypes';
import { ERoomType, FOfferType, IJsonQuery, IJsonQueryType } from './types';
import { isAvailableOfferTypeCombination } from './utils';

export function setOfferType(jsonQuery: IJsonQuery): (nextOfferType: FOfferType) => IJsonQuery {
  return nextOfferType => {
    if (!isAvailableOfferTypeCombination(nextOfferType)) {
      return jsonQuery;
    }

    const nextOfferTypes = getNextOfferTypes(nextOfferType);

    let nextJsonQuery = R.clone(jsonQuery);

    nextJsonQuery = resetOfferTypeRelatedTerms(nextJsonQuery)(nextOfferType);

    return nextOfferTypes.reduce((jsonQuery, offerType) => selectOfferType(jsonQuery)(offerType), nextJsonQuery);
  };
}

function getNextOfferTypes(nextOfferType: FOfferType): FOfferType[] {
  let nextOfferTypes = DISJOINT_AVAILABLE_OFFER_TYPES.filter(o => (o & nextOfferType) !== 0);

  if ([FOfferType.FlatOld, FOfferType.FlatNew].every(o => nextOfferTypes.includes(o))) {
    nextOfferTypes = nextOfferTypes.filter(o => (o & FOfferType.Flat) === 0);
    nextOfferTypes.push(FOfferType.Flat);
  }

  return nextOfferTypes;
}

function resetOfferTypeRelatedTerms(jsonQuery: IJsonQuery): (nextOfferType: FOfferType) => IJsonQuery {
  return () => {
    let nextJsonQuery = R.clone(jsonQuery);

    const room = getTermsValue('room')(nextJsonQuery);
    if (room) {
      nextJsonQuery = setRoomTypes(nextJsonQuery)(
        room.filter(r => ![ERoomType.Room, ERoomType.FlatShared, ERoomType.Bed].includes(r)),
      );
    }

    return nextJsonQuery;
  };
}

function selectOfferType(jsonQuery: IJsonQuery): (nextOfferType: FOfferType) => IJsonQuery {
  return nextOfferType => {
    let nextJsonQuery = R.clone(jsonQuery);

    switch (nextOfferType) {
      case FOfferType.Flat:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('flat');
        break;
      case FOfferType.FlatOld:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('flat');

        break;
      case FOfferType.FlatNew:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('flat');
        break;
      case FOfferType.FlatShared:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('flat');
        nextJsonQuery = appendRoomType(nextJsonQuery)(ERoomType.FlatShared);
        break;
      case FOfferType.Room:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('flat');
        nextJsonQuery = appendRoomType(nextJsonQuery)(ERoomType.Room);
        break;
      case FOfferType.Bed:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('flat');
        nextJsonQuery = appendRoomType(nextJsonQuery)(ERoomType.Bed);
        break;
    }

    return nextJsonQuery;
  };
}

function appendRoomType(jsonQuery: IJsonQuery) {
  return (roomType: ERoomType): IJsonQuery => {
    const roomTypes: ERoomType[] = getTermsValue('room')(jsonQuery) || [];

    return setTerms('room')(jsonQuery)(roomTypes.concat(roomType));
  };
}

function setRootOfferType(jsonQuery: IJsonQuery) {
  return (offerType: 'flat' | 'commercial' | 'suburban' | 'newobject'): IJsonQuery => {
    const currentType = jsonQuery._type;

    const nextType: IJsonQueryType = currentType.replace(
      /flat|commercial|suburban|newobject/,
      offerType,
    ) as IJsonQueryType;

    return setRootType(nextType)(jsonQuery);
  };
}
